import React, { useEffect, useRef } from 'react';

import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import { SeloPosEventoPorOrigem } from '../../selo-pos-evento/SeloPosEvento';
// O X do modal é o padrão compartilhado — sem este import a classe fica sem estilo e o botão
// sai como o nativo do navegador (foi exatamente o que aconteceu antes de 03/09/2026).
import '../../../shared/ui/modal-close-button.css';
import './AndamentoVeiculoModal.css';

interface AndamentoVeiculoModalProps {
  isOpen: boolean;
  carId: string | null;
  plate?: string | null;
  esteiraNome?: string | null;
  /** at-134 (ADR 0064): retorno de garantia? Só o fato, como na linha da tabela. */
  origemDoPosEvento?: 'INTERNO' | 'EXTERNO' | null;
  onClose: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Detalhe do veículo para o setor de EVENTOS (at-89, sobre o ADR 0039).
 *
 * 🔴 SOMENTE LEITURA, e com a projeção mínima do papel: placa, esteira atual e o que já foi feito.
 * Nada de proprietário, telefone, liberação, colaboradores ou observações internas — o ADR 0039
 * esconde isso de propósito, porque quem fala com o associado é a Recepção. O setor acompanha o
 * andamento; a novidade da at-89 é conseguir dizer O QUE já foi feito, não só em que etapa está.
 *
 * ⚠️ Autoria e data das marcações ficam ocultas (`ocultarAutoria`): o setor acompanha o serviço,
 * não as pessoas que o executaram.
 */
const AndamentoVeiculoModal: React.FC<AndamentoVeiculoModalProps> = ({
  isOpen,
  carId,
  plate,
  esteiraNome,
  origemDoPosEvento,
  onClose,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const aoClicarFora = (event: MouseEvent) => {
      const alvo = event.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onClose();
    };

    const aoTeclar = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [isOpen, onClose, sidebarRef]);

  if (!isOpen || !carId) return null;

  return (
    <div className="add-car-modal-overlay">
      <div className="andamento-veiculo-modal" ref={modalRef}>
        <div className="andamento-veiculo-modal-header">
          <div>
            <h2>
              {plate || 'Veículo'}
              {/* at-89: motivo da pendência ao lado da placa (Victor, 03/09/2026). */}
              <TagsDaEsteira carId={carId} esteiraAtual={esteiraNome} variante="inline" />
              <SeloPosEventoPorOrigem origem={origemDoPosEvento} />
            </h2>
            {esteiraNome && <span className="andamento-veiculo-esteira">{esteiraNome}</span>}
          </div>
          <button className="close-button" onClick={onClose} aria-label="Fechar" type="button">
            &times;
          </button>
        </div>

        <div className="andamento-veiculo-modal-body">
          <ProgressoSubtarefas carId={carId} ocultarAutoria />
          <ComentariosDoVeiculo carId={carId} ocultarAutoria />

          <p className="andamento-veiculo-hint">
            Esta tela é somente de consulta. As sub-tarefas são registradas pela equipe da oficina
            conforme o serviço avança; se ainda não houver nenhuma, é porque aquela etapa não tem
            detalhamento cadastrado.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AndamentoVeiculoModal;
